import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "../../competitionOperation"
import { invalidateEventCache } from "pages/EventsManagement/eventCachePolicy"

const pointsTexts = {
  POINTS_VERSION_CONFLICT: "Results or points changed. Reload and review the current values before saving.",
  INVALID_POINTS_ADJUSTMENT: "Review the adjustment and its reason. New totals must be whole numbers of zero or more.",
  INVALID_POINTS_TEAM: "A team adjustment no longer matches the team's players or points. Reload the current points and enter it again.",
  INVALID_POINTS_EMPTY: "No match participants are available to confirm. Reload the current points.",
  INVALID_POINTS_PARTICIPANT_LEVEL: "Some players have no level recorded for this competition, so points cannot be confirmed. Check their levels in their player profiles, then reload points.",
  INVALID_WAITING_LIST_CONFIRMATION: "The Waiting List confirmation was rejected, so nothing was saved. Reload the current points, review the Waiting List and confirm again.",
  ERROR_PERMISSIONS: "Your account no longer has permission to save points.",
  INVALID_POINTS_CONTEXT: "The event or competition could not be verified. Reload the page.",
  POINTS_CONTEXT_NOT_FOUND: "The event or competition is no longer available. Reload the current context.",
  INVALID_POINTS_REQUEST: "The request was rejected before saving. Review the current points and your adjustment before trying again.",
  MATCH_WRITE_OPERATION_CONFLICT: "This request has different content. Check its saved result before trying again.",
}
// Accounts refuses an invalid request with an INVALID_* code before anything is saved, so a code not listed here still reads as a refusal.
export const pointsMessage = code => pointsTexts[code]
  || (/^INVALID_/.test(code || "") ? pointsTexts.INVALID_POINTS_REQUEST : "The result could not be confirmed. Check the same request before saving again.")

// The words for a points or Action Logs read Accounts refused with a code (409 while a phase operation holds the competition);
// null leaves the screen's own "could not be loaded" text.
export const pointsReadMessage = code => ({
  PHASE_OPERATION_IN_PROGRESS: "A phase operation is in progress for this competition. Retry points when it finishes.",
}[code] || null)

export default function usePointsOperation({ userId, eventId, competitionId }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const key = `points:${userId}:${eventId}:${competitionId}`
  const active = useRef(null), retained = useRef(null), [state, setState] = useState({})
  const persist = useCallback(record => {
    try { sessionStorage.setItem(key, JSON.stringify(record)); return true } catch { return false }
  }, [key])
  // A 4xx on the first send was refused before anything was queued, so the runner ends it as an error with words. A rejected
  // retry cannot rule out an earlier commit, so retries (firstSubmission false) keep their unknown outcome.
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner.cancel()
    const runner = runCompetitionOperation({ operation, socket, submit, firstSubmission, responseTopic: "accounts-response", operationKind: "points-saved", messageForCode: pointsMessage,
      read: id => get(`/admin/point-allocations/${id}`) })
    const request = { operation, runner }; active.current = request
    retained.current = operation
    setState({ status: "pending", operation, restoredPayload: operation.payload, restoredOperationId: operation.operationId })
    runner.promise.then(result => {
      if (active.current !== request || result.status === "cancelled") return
      setState({ ...result, operation, ...(result.status !== "saved" && { restoredPayload: operation.payload, restoredOperationId: operation.operationId }) })
      if (result.status === "error") {
        active.current = null
        // The players a refusal names (INVALID_POINTS_PARTICIPANT_LEVEL) survive a reload with its words.
        retained.current = { ...operation, recovery: { status: "error", code: result.code, message: result.message, ...(Array.isArray(result.userIds) && { userIds: result.userIds }) } }
        persist(retained.current)
      }
      if (result.status === "saved") {
        invalidateEventCache({ eventId: operation.eventId, competitionId: operation.competitionId })
        active.current = null; retained.current = null
        try { sessionStorage.removeItem(key) } catch { /* Server retains the confirmed result. */ }
      }
    })
  }, [key, socket, persist])
  useEffect(() => {
    setState({}); retained.current = null
    if (userId) try {
      const op = JSON.parse(sessionStorage.getItem(key) || "null")
      if (op?.eventId === eventId && op?.competitionId === competitionId && /^[a-zA-Z0-9-]{16,128}$/.test(op.operationId || "") && op.correlationId && op.payload) {
        if (["error", "reviewed"].includes(op.recovery?.status)) {
          retained.current = op
          setState({ ...op.recovery, status: op.recovery.status === "reviewed" ? "idle" : "error", operation: op,
            restoredPayload: op.payload, restoredOperationId: op.operationId })
        } else observe(op)
      }
    } catch { /* Invalid recovery data cannot submit a command. */ }
    return () => { active.current?.runner.cancel(); active.current = null }
  }, [userId, key, eventId, competitionId, observe])
  const send = op => () => post(`/admin/competition-points/${competitionId}`, { ...op.payload, eventId, operationId: op.operationId, socketClientId }, { headers: { "X-Correlation-Id": op.correlationId } })
  const save = payload => {
    if (!userId || active.current || retained.current?.recovery?.status === "error") return
    const operation = { eventId, competitionId, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), payload }
    if (!persist(operation)) {
      setState({ status: "error", message: "Enable session storage to recover a save after reload." }); return
    }
    observe(operation, send(operation), true)
  }
  // Call only after reviewing the fresh context. The submitted draft survives
  // reload, but its rejected operation identity must not be reused for a new intent.
  const reconcile = () => {
    const previous = retained.current
    if (active.current || previous?.recovery?.status !== "error") return false
    const reviewed = { ...previous, recovery: { status: "reviewed" } }
    if (!persist(reviewed)) return false
    retained.current = reviewed
    setState({ status: "idle", restoredPayload: previous.payload, restoredOperationId: previous.operationId })
    return true
  }
  return { ...state, save, reconcile, needsReview: state.status === "error" && Boolean(state.operation),
    pending: ["pending", "unknown"].includes(state.status),
    recover: () => ["pending", "unknown"].includes(state.status) && state.operation && observe(state.operation, send(state.operation)) }

}
